
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { TimeUtil } from '@/helpers/TimeUtil'
import { appointmentStatusToName, appointmentStatusToColor, calcDuration, getOptimalTextColor, canceledAppointmentValues, templatesFilenameLanguage, pagesExportLimit } from '@/helpers/data'
import { commonModule, installationsModule, actionTypesModule, itemsModule, companyModule, appointmentsRecurrencesModule } from '@/store/modules/store-accessor'
import SignatureFields from '@/components/Sideview/SignatureFields.vue';
import Dropdown from '../../../components/dropdown/Dropdown.vue'
import DropdownItem from '../../../components/dropdown/DropdownItem.vue'
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";
import dayjs from 'dayjs';
import FileSaver from 'file-saver';

@Component({
  components: { SignatureFields, Dropdown, DropdownItem, ExportMenu }
})
export default class InstallationsSideviewAppointmentsTab extends Vue {
  @Prop() installation: Record<string, any>;
  @Prop() appointmentId: number;
  @Prop() recurrenceId: number;


  tableOptions = {sortBy: ['appointment_status_id', 'actual_start'], sortDesc: [false, false], itemsPerPage: 10, page: 1};

  appointmentStatusSortOrder: Record<number, number> = {
    2: 0, // pending
    3: 1, // ongoing
    1: 2, // canceled
    4: 3, // completed
  };

  isDownloadingExport = false;

  tableheaders: any [] = [
    { class:'font-weight-black', text: this.$t("action"), sortable: false, value: 'type_name'},
    { class:'font-weight-black', text: this.$t("item"), sortable: false, value: 'job_item' },
    { class:'font-weight-black', text: this.$t("user"), sortable: false, value: 'assignee_names' },
    { class:'font-weight-black', text: this.$t("scheduled_at"), sortable: false, value: 'actual_start'},
    { class:'font-weight-black', text: this.$t("duration"), sortable: false, value: 'duration' },
    { class:'font-weight-black', text: this.$t("status"), sortable: true, value: 'appointment_status_id' },
    { class:'', text: '', value: 'has_signature', width: '85px', sortable: false, align: 'right' },
    { class:'', text: '', value: 'data-table-expand' }, // Move expand arrow to the right
  ]

  expandedItems: any[] = [];
  isExpanded = false;

  mounted() {
    commonModule.getPersonnelCombo();
    commonModule.getPersonnelPartnersCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
    actionTypesModule.getActionTypesCombo();
    commonModule.getAppointmentsCancelReasonsCombo();
  }

  updated() {
    if (this.appointments.length > 0 && this.appointmentId && !this.isExpanded) {
      this.showExpansion();
      this.isExpanded = true;
    }
  }

  showExpansion() {
    const expansionAppointment = installationsModule?.currentInstallation?.appointments.find(i => i.id == this.appointmentId);  // This is needed to find the index in the expansion panel

    const sortBy = this.tableOptions.sortBy;
    const sortDesc = this.tableOptions.sortDesc;
    const sortedAppointments = this.sortAppointments(this.appointments, sortBy, sortDesc);

    const appointmentIndex = sortedAppointments.findIndex((a: any) => a.id == this.appointmentId);
    const page = Math.floor(appointmentIndex / this.tableOptions.itemsPerPage) + 1;
    this.tableOptions.page = page;

    if(this.hasAdvancedPlan) {
      const expansionAppointmentSeries = this.recurringSeries.find((r: any) => r.recurrence_id === this.recurrenceId ); // Find the recurrence in the expansion panel
      this.expandedSeries = [expansionAppointmentSeries];
    }

    this.expandedItems = [expansionAppointment];
  }

  @Watch("installation.info.id")
  updateAppointments() {
    commonModule.getPersonnelCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
    commonModule.initSnackbar({});
  }

  //item.scheduled_start && item.scheduled_end ? setDuration(item.scheduled_start, item.scheduled_end) : "-"
  get appointments() {
    if(!this.installation.appointments) return [];
    return this.installation.appointments;
  }

  get currInstallation () {
    return installationsModule.currentInstallation;
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  getFormattedAppointmentDuration(appointment: any) {
    if (appointment.appointment_status_id == 4) {
      if (appointment.actual_start && appointment.actual_end) {
        return this.setDuration(appointment.actual_start, appointment.actual_end);
      }
      else {
        return '-';
      }
    }
    else {
      if (appointment.scheduled_start && appointment.scheduled_end) {
        return this.setDuration(appointment.scheduled_start, appointment.scheduled_end);
      }
      else {
        return '-';
      }
    }

  }

  appointmentIsComplete(appointment: any) {
    if(appointment.appointment_status_id == 4)
      return true;
    else
      return false;
  }

  isRecurringAppointment(appointment: Record<string, any>) {
    return this.companyHasAdvancedPlan && appointment.recurrence_id != null;
  }

  addAppointment () {
    commonModule.showModal({ name: 'add-appointment-modal', payload: { installation: this.installation}})
  }

  editAppointment (appointment: any){
    appointment = {...appointment, client_email: this.installation.info.client_email};

    const modalName = appointment.is_virtual ? 'edit-recurring-appointment-modal' : 'edit-appointment-modal';

    commonModule.showModal({ name: modalName, payload: { appointment: appointment, inSideview: true }})
  }

  deleteAppointment (appointment: any){
    const modalName = appointment.is_virtual ? 'delete-recurring-appointment-modal' : 'delete-appointment-modal';
    commonModule.showModal({ name: modalName, payload: { appointment: appointment, inSideview: true }})
  }

  endAppointment (appointment: any){
    commonModule.showModal({ name: 'end-appointment-modal', payload: { appointment: appointment, installation: this.currInstallation }})
  }

  cancelAppointment (appointment: any){
    commonModule.showModal({ name: 'cancel-appointment-modal', payload: { appointment: appointment }})
  }

  formatDate (date: Date): any {
    if (date == undefined || date == null) {
      return '-'
    } else {
      return TimeUtil.formatDateTime(date)
    }
  }

  statusToName(status: string) {
    return appointmentStatusToName(status)
  }

  statusToColor(status: string){
    return appointmentStatusToColor(status)
  }

  setDuration(start: Date, end: Date) {
    return calcDuration(start, end);
  }

  get jobDescriptionIsEnabled() {
    return (companyModule.company?.preferences as any)?.job_description;
  }

  get tableIsLoading() {
    return installationsModule.sideviewTableIsLoading;
  }

  sortAppointments(items: any, sortBy: any, sortDesc: any) {
    if(!items) return [];
    // If sorting in tableOptions changes, so must this function
    return items.slice().sort((a: any, b: any) => {
      for (let i = 0; i < sortBy.length; i++) {
        const key = sortBy[i];
        const desc = sortDesc[i];

        let aVal;
        let bVal;

        if (key === 'appointment_status_id') {
          aVal = this.appointmentStatusSortOrder[a.appointment_status_id] ?? Number.MAX_SAFE_INTEGER;
          bVal = this.appointmentStatusSortOrder[b.appointment_status_id] ?? Number.MAX_SAFE_INTEGER;
        } else if (key === 'actual_start') {
          aVal = this.getAppointmentSortTime(a);
          bVal = this.getAppointmentSortTime(b);
        } else {
          aVal = a[key];
          bVal = b[key];
        }

        if (aVal == null && bVal != null) return desc ? 1 : -1;
        if (aVal != null && bVal == null) return desc ? -1 : 1;
        if (aVal == null && bVal == null) continue;

        const aDate = Date.parse(aVal);
        const bDate = Date.parse(bVal);
        const isDate = !isNaN(aDate) && !isNaN(bDate);

        if (isDate) {
          aVal = aDate;
          bVal = bDate;
        }

        if (aVal < bVal) return desc ? 1 : -1;
        if (aVal > bVal) return desc ? -1 : 1;
      }
      return 0;
    });
  }

  getAppointmentSortTime(appointment: Record<string, any>) {
    const sortDate = appointment?.appointment_status_id === 4 && appointment?.actual_start ? appointment.actual_start : appointment?.scheduled_start;

    return new Date(sortDate).getTime();
  }

  itemText(item: Record<string, any>) {
    const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;

    if(installationItemDescriptionIsEnabled) {
      const itemDescription = item.item_description && item.item_description.length > 0 ? item.item_description : '';
      return item.job_item && item.job_item.length > 0 ? `${itemDescription}${itemDescription ? ' | ' : ''}${item.job_item}` : '-'
    }

    return  item.job_item && item.job_item.length > 0 ? item.job_item : '-'
  }

  canceledAppointmentValues(item: Record<string, any>) {
    return canceledAppointmentValues(item);
  }

  // Recurrent Appointment - Start
  expandedSeries: any[] = []

  seriesHeaders: any [] = [
    { class:'font-weight-black', text: this.$t('action'), sortable: false, value: 'type_name'},
    { class:'font-weight-black', text: this.$t('item'), sortable: false, value: 'job_item' },
    { class:'font-weight-black', text: this.$t('user'), sortable: false, value: 'assignee_names' },
    { class:'font-weight-black', text: this.$t('recurrenceRepeatsMsg'), sortable: false, value: 'recurrence_label' },
    { class:'', text: '', value: 'data-table-expand' },
  ];

  get recurringSeries() {
    if (this.hasAdvancedPlan) {
      return this.installation?.recurrent_appointments ?? [];
    }
    return [];
  }

  formatEndDate(recurrence: any): string {
    if (!recurrence?.end_date) return '-';
    return TimeUtil.formatDateOnly(recurrence.end_date);
  }

  dateHasPassed(date: Date | string | null | undefined): boolean {
    if (date == null || date === '') return false;
    const parsed = dayjs(date);
    if (!parsed.isValid()) return false;
    return parsed.endOf('day').isBefore(dayjs());
  }

  recurrenceLabel(series: any): string {
    const r = series.recurrence;
    if (!r) return '';
    const { recurrence_type, interval, days_of_week } = r;
    const n = Number(interval) || 1;

    let text = '';
    if (recurrence_type === 'daily') {
      text = n === 1
        ? `${this.$t('every')} ${this.$t('recurrenceDayLowerCase')}`
        : `${this.$t('every')} ${n} ${this.$t('recurrenceDaysLowerCase')}`;
    } else if (recurrence_type === 'weekly') {
      text = n === 1 ? String(this.$t('everyWeek')) : `${this.$t('every')} ${n} ${this.$t('weeksLowerCase')}`;
    } else if (recurrence_type === 'monthly') {
      text = n === 1 ? String(this.$t('everyMonth')) : `${this.$t('every')} ${n} ${this.$t('monthsLowerCase')}`;
    } else if (recurrence_type === 'yearly') {
      text = n === 1 ? String(this.$t('everyYear')) : `${this.$t('every')} ${n} ${this.$t('yearsLowerCase')}`;
    }

    if (recurrence_type === 'weekly' && Array.isArray(days_of_week) && days_of_week.length) {
      const dayKeyMap: Record<string, string> = {
        MON: 'monday', TUE: 'tuesday', WED: 'wednesday',
        THU: 'thursday', FRI: 'friday', SAT: 'saturday', SUN: 'sunday',
      };
      const dayNames = days_of_week.map((d: string) => this.$t(dayKeyMap[d] || d));
      text += ` (${dayNames.join(', ')})`;
    }

    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  async editSeries(series: Record<string, any>) {
    const recurrenceInfo = await appointmentsRecurrencesModule.getAppointmentRecurrenceInfoById(series.recurrence_id);
    commonModule.showModal({ name: 'edit-recurring-appointment-series-modal', payload: { series: recurrenceInfo } });
  }

  deleteSeries(series: Record<string, any>) {
    commonModule.showModal({ name: 'delete-recurring-appointment-series-modal', payload: { recurrence_id: series.recurrence_id, installation_id: series.installation_id } });
  }

  appointmentHeaders: any[] = [
    { class:'font-weight-black', text: this.$t('action'), sortable: false, value: 'type_name' },
    { class:'font-weight-black', text: this.$t('scheduled_at'), sortable: false, value: 'scheduled_start' },
    { class:'font-weight-black', text: this.$t('duration'), sortable: false, value: 'duration' },
    { class:'', text: '', sortable: false, value: 'appointment-options' },
  ];

  expandedAppointmentsMap: Record<string | number, any[]> = {}

  ensureExpandedAppointments(recurrenceId: string | number) {
    if (!this.expandedAppointmentsMap[recurrenceId]) {
      this.$set(this.expandedAppointmentsMap, recurrenceId, [])
    }
    return this.expandedAppointmentsMap[recurrenceId]
  }
  // Recurrent Appointment - Stop

  get hasAdvancedPlan() {
    return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
  }

  get companyHasAdvancedPlan() {
    return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
  }

  downloadExportLengthCheck(type: string) {
    if(type === 'pdf') {
      return this.currInstallation?.appointments?.length > 0 && this.currInstallation?.appointments?.length <= pagesExportLimit();
    }
    return this.currInstallation?.appointments?.length > 0;
  }

  async donwloadAppointmentsExport(format: string) {
    this.isDownloadingExport = true;
    const userTimezone = dayjs.tz.guess();

    const data = {
      user_timezone: userTimezone,
      language: localStorage.getItem("lang"),
      format: format,
      installation_id: this.currInstallation.info.id,
    };

    const filetype = format === 'pdf' ? 'pdf' : 'xlsx'

    try {
      const company = await companyModule.getCompanyInfo();
      const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_${data.installation_id}_appointments_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.${filetype}`.replace( / /g, '_');

      const pdf = await installationsModule.getInstallationSideviewAppointmentsExport(data);

      const file = new File([pdf], filename);
      FileSaver.saveAs(file);
    } finally {
      this.isDownloadingExport = false;
    }
  }

};
